import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { formatToTwoDecimal } from "../../../../utils/numberFormatter";
import {
  displayAmount,
  RANGE_COLUMNS,
  SIGNATORIES,
  SUMMARY_COLUMNS,
  transactionCountsBy,
} from "./accountSummaryData";

// PDF version of the Petty Cash Summary layout (see accountSummaryData.js).
// Built-in Helvetica has no peso sign or Chinese characters, so amounts are
// plain numbers and Chinese in the data won't print correctly.

// Column widths, same order as SUMMARY_COLUMNS
const WIDTHS = ["6%", "24%", "22%", "34%", "14%"];

const styles = StyleSheet.create({
  page: { padding: 24, fontSize: 8, fontFamily: "Helvetica" },
  // Logo | centered details | same-width spacer (keeps the text centered).
  // The row grows to fit the logo, so paddingBottom is always the gap
  // between the logo and the divider line
  letterhead: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  logoBox: { width: 95 },
  logo: { width: 95, height: 64, objectFit: "contain" },
  letterheadText: { flex: 1 },
  company: { fontSize: 28, fontFamily: "Helvetica-Bold", textAlign: "center" },
  companyLine: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginTop: 1,
  },
  dayDate: { textAlign: "right", fontSize: 9, marginTop: 6, marginBottom: 3 },
  titleBand: {
    backgroundColor: "#000",
    color: "#fff",
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    padding: 4,
    fontSize: 9,
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderColor: "#999",
  },
  headerRow: { backgroundColor: "#f2f2f2", fontFamily: "Helvetica-Bold" },
  cell: { padding: 3, borderRightWidth: 0.5, borderColor: "#999" },
  right: { textAlign: "right" },
  bold: { fontFamily: "Helvetica-Bold" },
  deposit: { color: "#198754" },
  withdraw: { color: "#dc3545" },
  balanceRow: { backgroundColor: "#ffc000" },
  // flex-start: each table is only as tall as its own rows (stretching would
  // draw the shorter table's left border down to the taller one)
  countTables: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginTop: 10,
    gap: 40,
  },
  countTable: {
    width: "35%",
    borderTopWidth: 0.5,
    borderLeftWidth: 0.5,
    borderColor: "#999",
  },
  pageNumber: {
    position: "absolute",
    bottom: 10,
    right: 24,
    fontSize: 8,
    color: "#555",
  },
  signatures: { flexDirection: "row", marginTop: 30 },
  signatureBox: { width: "33%", paddingRight: 20 },
  signatureLine: {
    borderBottomWidth: 0.5,
    borderColor: "#000",
    marginTop: 24,
  },
});

const Cell = ({ index, children, style }) => (
  <Text style={[styles.cell, { width: WIDTHS[index] }, style]}>{children}</Text>
);

// Label spans the first four columns, value sits in the Amount column
const SummaryRow = ({ label, value, style }) => (
  <View style={[styles.row, style]}>
    <Text style={[styles.cell, styles.bold, styles.right, { width: "86%" }]}>
      {label}
    </Text>
    <Text style={[styles.cell, styles.bold, styles.right, { width: "14%" }]}>
      {value}
    </Text>
  </View>
);

// Logo top-left, name + details centered
const Letterhead = ({ company }) => (
  <View style={styles.letterhead}>
    <View style={styles.logoBox}>
      {company.logo && <Image src={company.logo} style={styles.logo} />}
    </View>
    <View style={styles.letterheadText}>
      <Text style={styles.company}>{company.name}</Text>
      {company.lines.map((line) => (
        <Text key={line} style={styles.companyLine}>
          {line}
        </Text>
      ))}
    </View>
    <View style={styles.logoBox} />
  </View>
);

// "Page 1 of 2" in the bottom-right corner of every printed page
const PageNumber = () => (
  <Text
    style={styles.pageNumber}
    fixed
    render={({ pageNumber, totalPages }) =>
      `Page ${pageNumber} of ${totalPages}`
    }
  />
);

const Signatures = () => (
  <View style={styles.signatures} wrap={false}>
    {SIGNATORIES.map((label) => (
      <View key={label} style={styles.signatureBox}>
        <Text style={styles.bold}>{label}</Text>
        <View style={styles.signatureLine} />
      </View>
    ))}
  </View>
);

// One day's summary; starts on its own page
const DayPage = ({
  summary,
  title,
  company,
  showSignatures,
}) => (
  <Page size="A4" orientation="landscape" style={styles.page}>
    <Letterhead company={company} />
    <Text style={styles.dayDate}>{summary.date}</Text>

    <Text style={styles.titleBand}>
      {title} {summary.label}
    </Text>

    {/* Wrapper keeps the repeating (fixed) header to the pages the table is
        on, so a page holding only the signatures gets no empty header */}
    <View>
      <View style={[styles.row, styles.headerRow]} fixed>
        {SUMMARY_COLUMNS.map((column, index) => (
          <Cell
            key={column}
            index={index}
            style={index === 4 ? styles.right : undefined}
          >
            {column}
          </Cell>
        ))}
      </View>

      <SummaryRow
        label="CASH BALANCE"
        value={formatToTwoDecimal(summary.openingBalance)}
      />

      {summary.rows.map((row) => (
        <View key={row.ref} style={styles.row} wrap={false}>
          <Cell index={0}>{row.ref}</Cell>
          <Cell index={1}>{row.payee}</Cell>
          <Cell index={2}>{row.accountTitle}</Cell>
          <Cell index={3}>{row.particulars}</Cell>
          <Cell
            index={4}
            style={[
              styles.right,
              row.type === "Deposit" ? styles.deposit : styles.withdraw,
            ]}
          >
            {displayAmount(row)}
          </Cell>
        </View>
      ))}

      <SummaryRow
        label="TOTAL WITHDRAW"
        value={formatToTwoDecimal(summary.totalWithdraw)}
      />
      <SummaryRow
        label="TOTAL DEPOSIT"
        value={formatToTwoDecimal(summary.totalDeposit)}
      />
      <SummaryRow
        label="BALANCE"
        value={formatToTwoDecimal(summary.balance)}
        style={styles.balanceRow}
      />
    </View>

    {showSignatures && <Signatures />}
    <PageNumber />
  </Page>
);

// Summary layout: the whole range in one table (no tabs); pages break
// wherever the rows run out of room. Widths follow RANGE_COLUMNS
const RANGE_WIDTHS = ["8%", "5%", "14%", "16%", "25%", "10%", "10%", "12%"];

const RangeCell = ({ index, children, style }) => (
  <Text style={[styles.cell, { width: RANGE_WIDTHS[index] }, style]}>
    {children}
  </Text>
);

// Label in Particulars, values in Cash In / Cash Out / Balance
const RangeTotalRow = ({ date, label, cashIn, cashOut, balance, style }) => (
  <View style={[styles.row, styles.bold]} wrap={false}>
    <RangeCell index={0}>{date}</RangeCell>
    <RangeCell index={1} />
    <RangeCell index={2} />
    <RangeCell index={3} />
    <RangeCell index={4} style={styles.right}>
      {label}
    </RangeCell>
    <RangeCell index={5} style={styles.right}>
      {cashIn}
    </RangeCell>
    <RangeCell index={6} style={styles.right}>
      {cashOut}
    </RangeCell>
    <RangeCell index={7} style={[styles.right, style]}>
      {balance}
    </RangeCell>
  </View>
);

// Each value once (A-Z) with its transaction count, same as the side tables
// in the Summary Excel sheet
const CountTable = ({ header, entries }) => (
  <View style={styles.countTable}>
    <View style={[styles.row, styles.headerRow]} wrap={false}>
      <Text style={[styles.cell, { width: "78%" }]}>{header}</Text>
      <Text style={[styles.cell, styles.right, { width: "22%" }]}>TOTAL</Text>
    </View>
    {entries.map(({ name, total }) => (
      <View key={name} style={styles.row} wrap={false}>
        <Text style={[styles.cell, { width: "78%" }]}>{name}</Text>
        <Text style={[styles.cell, styles.right, { width: "22%" }]}>
          {total}
        </Text>
      </View>
    ))}
  </View>
);

// Transactions per page in the Summary PDF
const ROWS_PER_PAGE = 20;

const chunk = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks.length ? chunks : [[]];
};

// Same top of page on every Summary page: letterhead, period, title band
const RangePageHeader = ({ company, period, title }) => (
  <>
    <Letterhead company={company} />
    <Text style={styles.dayDate}>{period}</Text>
    <Text style={styles.titleBand}>{title}</Text>
  </>
);

const RangeTransactionRow = ({ row }) => (
  <View style={styles.row} wrap={false}>
    <RangeCell index={0}>{row.date}</RangeCell>
    <RangeCell index={1}>{row.ref}</RangeCell>
    <RangeCell index={2}>{row.payee}</RangeCell>
    <RangeCell index={3}>{row.accountTitle}</RangeCell>
    <RangeCell index={4}>{row.particulars}</RangeCell>
    <RangeCell index={5} style={[styles.right, styles.deposit]}>
      {row.cashIn ? formatToTwoDecimal(row.cashIn) : ""}
    </RangeCell>
    <RangeCell index={6} style={[styles.right, styles.withdraw]}>
      {row.cashOut ? formatToTwoDecimal(row.cashOut) : ""}
    </RangeCell>
    <RangeCell index={7} style={styles.right}>
      {formatToTwoDecimal(row.balance)}
    </RangeCell>
  </View>
);

// Pages of ROWS_PER_PAGE transactions, each with the full page header and
// column headers; CASH BALANCE opens the first page, TOTAL and the signatures
// close the last. The download then adds a page with only the NAME /
// ACCOUNT TITLE counts. The preview passes showSignatures={false} and
// showCountTables={false}
export const RangeSummaryPDF = ({
  summary,
  title,
  company,
  period,
  showSignatures = true,
  showCountTables = true,
}) => {
  const pages = chunk(summary.rows, ROWS_PER_PAGE);
  const pageHeader = (
    <RangePageHeader company={company} period={period} title={title} />
  );

  return (
    <Document>
      {pages.map((rows, pageIndex) => {
        const isFirst = pageIndex === 0;
        const isLast = pageIndex === pages.length - 1;

        return (
          <Page
            key={pageIndex}
            size="A4"
            orientation="landscape"
            style={styles.page}
          >
            {pageHeader}

            <View style={[styles.row, styles.headerRow]}>
              {RANGE_COLUMNS.map((column, index) => (
                <RangeCell
                  key={column}
                  index={index}
                  style={index >= 5 ? styles.right : undefined}
                >
                  {column}
                </RangeCell>
              ))}
            </View>

            {isFirst && (
              <RangeTotalRow
                label="CASH BALANCE"
                balance={formatToTwoDecimal(summary.openingBalance)}
              />
            )}

            {rows.map((row) => (
              <RangeTransactionRow key={row.ref} row={row} />
            ))}

            {isLast && (
              <RangeTotalRow
                label="TOTAL"
                cashIn={formatToTwoDecimal(summary.totalCashIn)}
                cashOut={formatToTwoDecimal(summary.totalCashOut)}
                balance={formatToTwoDecimal(summary.balance)}
                style={styles.balanceRow}
              />
            )}

            {isLast && showSignatures && <Signatures />}
            <PageNumber />
          </Page>
        );
      })}

      {showCountTables && (
        <Page size="A4" orientation="landscape" style={styles.page}>
          {pageHeader}
          <View style={styles.countTables}>
            <CountTable
              header="NAME"
              entries={transactionCountsBy(summary.rows, "payee")}
            />
            <CountTable
              header="ACCOUNT TITLE"
              entries={transactionCountsBy(summary.rows, "accountTitle")}
            />
          </View>
          <PageNumber />
        </Page>
      )}
    </Document>
  );
};

// `days` comes from buildDailySummaries - one page section per day.
// The preview passes showSignatures={false}; the download keeps them.
const AccountSummaryPDF = ({ days, showSignatures = true, ...header }) => (
  <Document>
    {days.map((summary) => (
      <DayPage
        key={summary.label}
        summary={summary}
        showSignatures={showSignatures}
        {...header}
      />
    ))}
  </Document>
);

export default AccountSummaryPDF;
